import { useEffect, useState, useRef } from 'react';

type Props = {
  onInView?: VoidFunction;
  options?: IntersectionObserverInit;
  disabled?: boolean;
  // @default 1000
  // this allows you to throttle the onInView callback, preventing extra api calls
  throttleTime?: number;
};

const elementFoundTimeMap = new WeakMap<Element, number>();

function wasFoundRecently(element: Element, throttleTime: number) {
  return (
    elementFoundTimeMap.has(element) &&
    elementFoundTimeMap.get(element)! + throttleTime > Date.now()
  );
}

export const useInView = ({ disabled, onInView, options, throttleTime = 1000 }: Props) => {
  const [ref, setRef] = useState<Element | null>(null);
  const _throttleTime = useRef(1000);
  _throttleTime.current = throttleTime;

  useEffect(() => {
    if (!ref) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !disabled &&
          !wasFoundRecently(entries[0].target, _throttleTime.current)
        ) {
          onInView?.();
          if (_throttleTime.current) {
            elementFoundTimeMap.set(entries[0].target, Date.now());
          }
        }
      },
      {
        ...options,
      },
    );

    observer.observe(ref);

    return () => {
      observer.disconnect();
    };
  }, [disabled, onInView, ref, options]);

  return { ref: setRef };
};
